import {selectDropdown} from '../util/ui';
import {type ICurrencyConfiguration} from '../../../@type/woocommerce/currency-configuration';
import {type IDictionary} from '../../../@type/dictionary';
import {Feature, setFeatureSupport, startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {store} from '../store';
import {MerchantConfiguration, updateMerchantCurrencyConfig} from '../reducers/merchantConfigurationReducer';
import {requestCartCalculation} from '../payment/order';
import {getLocaleText} from '../util/translation';
import {PeachPayCustomer} from '../reducers/peachPayCustomerReducer';
import {type CurrencyDefaultsTo, type ICurrencySwitcherFeatureConfig} from '../models/ICurrencySwitcherFeatureConfig';
import {$qsAll, $qs} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {setSelectedPaymentGateway} from '../reducers/paymentConfigurationReducer';
import {SDKFlags} from '../sdk';
import {peachpayUpdateCurrencyCookie} from '../../../@shared/ts/currency';
import {captureSentryException} from '../../../@shared/ts/sentry';
import {type IResponse} from '../../../@type/response';

/**
 * Initializes the currency switch function for peachpay
 */
export function initCurrencySwitcher() {
	if (!Feature.enabled(FeatureFlag.CURRENCY_SWITCHER_INPUT)) {
		return;
	}

	window.addEventListener('pp-update-currency-switcher-feature', updateCurrencySwitcherFeature);
	renderCurrencySelector();
}

/**
 * Renders the currency selector in the modal with all it's options
 */
function renderCurrencySelector(): void {
	// Make sure any previous versions of the dropdown are deleted on render
	$qsAll('#pp_currency_select, #pp_currency_select_div', $removeSelector => {
		$removeSelector.remove();
	});

	// Insertion locations
	const $insertionLocationNew = $qs('#pp-summary-body')?.parentElement;
	const $insertionLocationMobile = $qs('#pp-pms-new-container');

	// Required globals
	const currencyInfo = Feature.metadata<Record<string, ICurrencyConfiguration>>(FeatureFlag.CURRENCY_SWITCHER_INPUT, 'currency_info');
	if (!currencyInfo || Object.keys(currencyInfo).length < 2) {
		return;
	}

	$insertionLocationNew?.insertAdjacentElement('afterend', buildCurrencySelectDiv(currencyInfo));
	$insertionLocationMobile?.insertAdjacentElement('afterend', buildCurrencySelectDiv(currencyInfo, 'pp-currency-mobile'));
}

function buildCurrencySelectDiv(data: Record<string, ICurrencyConfiguration>, customClasses = ''): Element {
	const $options = renderCurrencyList(getCurrencyDropDownInfo(data), MerchantConfiguration.currency.code());

	// Selector div and title creation
	const currencyContainer = document.createElement('div');
	const $currencySelectTitle = document.createElement('span');
	$currencySelectTitle.innerHTML = getLocaleText('Currency');
	$currencySelectTitle.setAttribute('class', 'pp-title');
	currencyContainer.id = 'pp_currency_select_div';
	currencyContainer.setAttribute('class', 'pp-section-mb ' + customClasses);
	currencyContainer.append($currencySelectTitle);

	const $currencySelect = document.createElement('select');
	$currencySelect.innerHTML = $options;
	$currencySelect.classList.add('pp-currency-selector');
	selectDropdown($currencySelect, MerchantConfiguration.currency.code());
	const selectContainer = document.createElement('div');
	selectContainer.classList.add('pp-currency-selector-container');
	selectContainer.append($currencySelect);
	currencyContainer.append(selectContainer);

	// Event listeners for the dropdowns
	$currencySelect.addEventListener('change', currencyEventListener);

	return currencyContainer as Element;
}

function getCurrencyDropDownInfo(currencyInfo: Record<string, ICurrencyConfiguration>): Record<string, string> {
	// Dropdown list data
	const mappedCurrencies = {} as Record<string, string>;
	for (const [key, currency] of Object.entries(currencyInfo)) {
		if (currency?.hidden) {
			mappedCurrencies[key + ' disabled'] = `(${currency.symbol}) - ${currency.name}`;
		} else {
			mappedCurrencies[key] = `(${currency.symbol}) - ${currency.name}`;
		}
	}

	return mappedCurrencies;
}

/**
 * This function fires off a new Type of peachpay event called the currency Switch Event
 * This event will update the cookie on the woocommerce side allowing recalculation of Cart totals and order values to be correctly
 * Displayed
 * @param currency (the currency we will past to the window)
 */
function sendCurrencySwitchMessage(currency: string): void {
	peachpayUpdateCurrencyCookie(currency);
	SDKFlags.setReload();
}

async function currencyEventListener(event: Event) {
	event.preventDefault();

	const currencyInfo = Feature.metadata<Record<string, ICurrencyConfiguration>>(FeatureFlag.CURRENCY_SWITCHER_INPUT, 'currency_info');
	const $target = event.target as HTMLSelectElement;
	$target.blur();
	if (currencyInfo?.[$target.value] && $target.value !== MerchantConfiguration.currency.code()) {
		store.dispatch(startModalLoading());

		store.dispatch(updateMerchantCurrencyConfig({
			...MerchantConfiguration.currency.configuration(),
			code: (currencyInfo?.[$target.value]?.code ?? MerchantConfiguration.currency.code()),
		}));

		// This event is to update the currency on the php side so we get the right currency in the modal
		sendCurrencySwitchMessage($target.value);

		await requestCartCalculation('push', 'currency-switcher:change');

		const config = currencyInfo?.[$target.value];
		if (config) {
			store.dispatch(updateMerchantCurrencyConfig(config));
		}

		store.dispatch(stopModalLoading());
	}
}

/**
 * Generates a dropdown html select list for currencies since the default should not be the printed value it should be the selected value
 */
function renderCurrencyList(data: IDictionary, defaultOption = ''): string {
	if (!data) {
		data = {};
	}

	const list = Object.entries(data).map(([key, value]) => `<option value=${key} ${(key === defaultOption) ? 'selected' : ''}> ${value} </option>`);

	return list.join('');
}

/**
 * Syncs merchant currency config info with currency cookie if cookie changed
 * since last time site reloaded.
 */
function refreshCurrencySelected() {
	const currency = MerchantConfiguration.currency.code();
	const currencyInfo = Feature.metadata<Record<string, ICurrencyConfiguration>>(FeatureFlag.CURRENCY_SWITCHER_INPUT, 'currency_info');

	if (!currency || currencyInfo === null || !(currency in currencyInfo)) {
		return;
	}

	sendCurrencySwitchMessage(currency);
}

/**
 * Returns false if feature enabled, otherwise returns CurrencyDefaultsTo
 */
function getCurrencyDefaultTo(): CurrencyDefaultsTo | null {
	return Feature.metadata<CurrencyDefaultsTo>(FeatureFlag.CURRENCY_SWITCHER_INPUT, 'how_currency_defaults');
}

/**
 * Retrieves and updates selected currency and currency switcher to new modal data for a given country
 * @param country Country code string
 */
async function updateCurrencySwitcherForCountry(country: string) {
	const response = await fetch('/?wc-ajax=pp-get-modal-currency-data', {
		method: 'GET',
		headers: {
			credentials: 'same-origin',
			'currency-country': country,
		},
	});

	if (!response.ok) {
		const error = new Error('Unable to retrieve country currency');
		captureSentryException(error);
		throw error;
	}

	const result = await response.json() as IResponse<ICurrencySwitcherFeatureConfig>;
	if (!result.success && !result.data) {
		return;
	}

	const countryCSinput = result.data;

	if (!countryCSinput) {
		return;
	}

	const switcherEnabledForCountry = countryCSinput.enabled;
	const curFeatureState = store.getState().environment.plugin.featureSupport;

	curFeatureState['currency_switcher_input'] = countryCSinput;
	store.dispatch(setFeatureSupport(curFeatureState));

	if (!switcherEnabledForCountry && countryCSinput.metadata.active_currency) {
		sendCurrencySwitchMessage(countryCSinput.metadata.active_currency.code);
		store.dispatch(updateMerchantCurrencyConfig(countryCSinput.metadata.active_currency));
	} else if (switcherEnabledForCountry && !(MerchantConfiguration.currency.code() in countryCSinput.metadata.currency_info)) {
		sendCurrencySwitchMessage(countryCSinput.metadata.set_cur);
		const config = countryCSinput.metadata.currency_info?.[countryCSinput.metadata.set_cur];
		if (config) {
			store.dispatch(updateMerchantCurrencyConfig(config));
		}
	} else {
		sendCurrencySwitchMessage(MerchantConfiguration.currency.code());
	}
}

/**
 * Handles the pp-update-currency-switcher-feature event. Refreshes currency switcher data
 * as needed.
 */
async function updateCurrencySwitcherFeature() {
	store.dispatch(startModalLoading());

	if (getCurrencyDefaultTo() === 'billing_country' && PeachPayCustomer.billing.country() && PeachPayCustomer.billing.country() !== '') {
		peachpayUpdateCurrencyCookie(PeachPayCustomer.billing.country());

		await updateCurrencySwitcherForCountry(PeachPayCustomer.billing.country());
	}

	await requestCartCalculation('push', 'currency-switcher:feature-update');
	renderCurrencySelector();

	refreshCurrencySelected();
	store.dispatch(stopModalLoading());
}

function setupCurrencyFallbackEvents() {
	$qs('body')?.addEventListener('click', async event => {
		const $target = event.target as HTMLElement;
		const $button = $target?.closest<HTMLElement>('.currency-fallback-button');
		if (!$button) {
			return;
		}

		const gatewayId = $button.dataset['gateway'] ?? '';
		const defaultCurrency = $button.dataset['currency'] ?? '';

		const currencyInfo = Feature.metadata<Record<string, ICurrencyConfiguration>>(FeatureFlag.CURRENCY_SWITCHER_INPUT, 'currency_info');
		const config = currencyInfo?.[defaultCurrency];
		if (!config) {
			return;
		}

		store.dispatch(startModalLoading());
		store.dispatch(updateMerchantCurrencyConfig(config));
		peachpayUpdateCurrencyCookie(defaultCurrency);

		store.dispatch(setSelectedPaymentGateway(gatewayId));

		await requestCartCalculation('push', 'currency-fallback:change');

		$qsAll<HTMLSelectElement>('.pp-currency-selector', $el => {
			selectDropdown($el, defaultCurrency);
		});

		store.dispatch(stopModalLoading());
	});
}

export {
	setupCurrencyFallbackEvents,
};
